import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import { parseTimeRange } from 'utils/timeUtils'

// ─── Helpers ───────────────────────────────────────────────────────────────────

const formatAED = (amount) => `AED ${parseFloat(amount || 0).toFixed(2)}`

const formatDubaiDate = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-AE', {
        day: '2-digit', month: 'long', year: 'numeric',
        timeZone: 'Asia/Dubai'
    })
}

const formatDubaiDateTime = () => {
    return new Date().toLocaleString('en-AE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
        timeZone: 'Asia/Dubai'
    })
}

const resolveTime = (order) => {
    // custom_time: "12:02 – 18:08", preparationtime can be null
    const raw = order?.time_type === 'custom'
        ? order?.custom_time
        : order?.preparationtime?.time || null

    const { from, to } = parseTimeRange(raw)
    if (!from) return '—'
    const label = order?.time_type === 'custom' ? ' (Custom)' : ''
    return to ? `${from} – ${to}${label}` : `${from}${label}`
}

// item:  { quantity, unit, price, discount_amount, subtotal, total, product: { name, item_code } }
const getItemName    = (item) => item?.product?.name         || '—'
const getItemCode    = (item) => item?.product?.item_code    || '—'
const getItemQty     = (item) => `${parseFloat(item?.quantity || 0)} ${item?.unit || ''}`
const getUnitPrice   = (item) => parseFloat(item?.price      || 0).toFixed(2)
const getItemTotal   = (item) => parseFloat(item?.total      || 0).toFixed(2)
const hasDiscount    = (item) => parseFloat(item?.discount_amount || 0) > 0

// ─── Thermal Layout ────────────────────────────────────────────────────────────
export const ThermalOrderLayout = forwardRef(({ order, size = '80' }) => {
    const items    = order?.items || []
    const subtotal = parseFloat(order?.subtotal || 0)
    const discount = parseFloat(order?.discount_amount || 0)
    const total    = parseFloat(order?.total || 0)

    return (
        <div className={`thermal-invoice thermal thermal-${size}`}>

            {/* Store Header */}
            <div className="center bold large mb">YOUR STORE NAME</div>
            <div className="center small">
                Dubai, United Arab Emirates<br />
                TRN: 100XXXXXXXXX<br />
                Tel: +971 XX XXX XXXX
            </div>

            <hr className="double-line" style={{ margin: '8px 0' }} />

            {/* Order Meta */}
            <div className="section">
                <div className="row"><span className="bold">Order #:</span>      <span>{order?.order_number}</span></div>
                <div className="row"><span className="bold">Order Date:</span>   <span>{formatDubaiDate(order?.order_date)}</span></div>
                <div className="row"><span className="bold">Delivery:</span>     <span>{formatDubaiDate(order?.delivery_date)}</span></div>
                <div className="row"><span className="bold">Pref. Time:</span>   <span>{resolveTime(order)}</span></div>
                <div className="row"><span className="bold">Status:</span>       <span style={{ textTransform: 'capitalize' }}>{order?.status}</span></div>
            </div>

            <hr className="double-line" style={{ margin: '8px 0' }} />

            {/* Customer */}
            <div className="section">
                <div className="row"><span className="bold">Customer:</span> <span>{order?.customer?.name || '—'}</span></div>
                <div className="row"><span className="bold">WhatsApp:</span> <span>{order?.customer?.whatsapp_number || '—'}</span></div>
                {order?.customer?.email && (
                    <div className="row"><span className="bold">Email:</span> <span>{order?.customer?.email}</span></div>
                )}
                <div className="row"><span className="bold">Address:</span></div>
                <div className="small" style={{ paddingLeft: '4px', marginTop: '2px' }}>
                    {order?.customer?.address || '—'}
                </div>
            </div>

            <hr className="double-line" style={{ margin: '8px 0' }} />

            {/* Items Table */}
            <table className="items-table" width="100%">
                <thead>
                    <tr>
                        <th style={{ width: '8%',  borderLeft: '1px solid #000', paddingLeft: '4px' }}>#</th>
                        <th style={{ width: '38%' }}>ITEM</th>
                        <th className="right" style={{ width: '20%' }}>QTY</th>
                        <th className="right" style={{ width: '34%' }}>AMT (AED)</th>
                    </tr>
                </thead>
                <tbody style={{ borderBottom: '1px solid #000' }}>
                    {items.map((item, i) => (
                        <tr key={i} className="item-main">
                            <td style={{ borderLeft: '1px solid #000', paddingLeft: '4px' }}>{i + 1}</td>
                            <td>
                                {getItemName(item)}
                                {hasDiscount(item) && (
                                    <div className="small" style={{ color: '#666' }}>
                                        Disc: AED {parseFloat(item.discount_amount).toFixed(2)}
                                    </div>
                                )}
                            </td>
                            <td className="right">{getItemQty(item)}</td>
                            <td className="right">{getItemTotal(item)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={{ margin: '12px 0' }} />

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: '65%' }}>
                    <div className="row right" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Sub Total:</span>
                        <span>{formatAED(subtotal)}</span>
                    </div>
                    {discount > 0 && (
                        <div className="row right" style={{ display: 'flex', justifyContent: 'space-between', color: 'red' }}>
                            <span>Discount:</span>
                            <span>-{formatAED(discount)}</span>
                        </div>
                    )}
                    <hr className="double-line" />
                    <div className="row bold large right" style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>TOTAL:</span>
                        <span>{formatAED(total)}</span>
                    </div>
                    <hr />
                </div>
            </div>

            {/* Notes */}
            {order?.notes && (
                <>
                    <hr style={{ margin: '6px 0' }} />
                    <div className="section small">
                        <span className="bold">Notes: </span>{order.notes}
                    </div>
                </>
            )}

            <hr className="double-line" style={{ margin: '8px 0' }} />

            {/* Footer */}
            <div className="center small mt">
                Thank you for your order!<br />
                شكراً لطلبك
            </div>
            <div className="center small" style={{ marginTop: '6px' }}>{formatDubaiDateTime()}</div>
            <div className="center small" style={{ marginTop: '4px' }}>*** ORIGINAL COPY ***</div>
        </div>
    )
})

// ─── A4 Layout ─────────────────────────────────────────────────────────────────
export const A4OrderLayout = forwardRef(({ order }, ref) => {
    const items    = order?.items    || []
    const subtotal = parseFloat(order?.subtotal       || 0)
    const discount = parseFloat(order?.discount_amount || 0)
    const total    = parseFloat(order?.total           || 0)

    const statusColors = {
        pending:    { bg: '#fff7e6', color: '#d48806' },
        processing: { bg: '#e6f4ff', color: '#1677ff' },
        confirmed:  { bg: '#f6ffed', color: '#52c41a' },
        delivered:  { bg: '#f0fff0', color: '#389e0d' },
        cancelled:  { bg: '#fff2f0', color: '#ff4d4f' },
    }
    const statusStyle = statusColors[order?.status] || { bg: '#fafafa', color: '#8c8c8c' }

    return (
        <div ref={ref} className="a4-page">

            {/* Header */}
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                    <h1 style={{ fontSize: '22px', margin: '0 0 4px', color: '#1a1a1a' }}>XION</h1>
                    <p style={{ margin: '2px 0', fontSize: '13px', color: '#595959' }}>Dubai, United Arab Emirates</p>
                    <p style={{ margin: '2px 0', fontSize: '12px', color: '#595959' }}>Tel: +971 50 123 4567</p>
                </div>

                <div style={{ textAlign: 'right', fontSize: '13px' }}>
                    <h2 style={{ fontSize: '20px', color: '#595959', margin: '0 0 8px' }}>ORDER RECEIPT</h2>
                    <div style={{ color: '#595959' }}><strong style={{ color: '#1a1a1a' }}>Order #:</strong> {order?.order_number}</div>
                    <div style={{ color: '#595959' }}><strong style={{ color: '#1a1a1a' }}>Order Date:</strong> {formatDubaiDate(order?.order_date)}</div>
                    {/* <div style={{ color: '#595959' }}><strong style={{ color: '#1a1a1a' }}>Delivery Date:</strong> {formatDubaiDate(order?.delivery_date)}</div> */}
                    <div style={{ color: '#595959' }}><strong style={{ color: '#1a1a1a' }}>Preferred Time:</strong> {resolveTime(order)}</div>
                    <div style={{ marginTop: '8px' }}>
                        <span style={{
                            padding: '3px 12px', borderRadius: '12px',
                            fontSize: '12px', fontWeight: 700,
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                        }}>
                            {String(order?.status || '').toUpperCase()}
                        </span>
                    </div>
                </div>
            </header>

            <hr style={{ margin: '0 0 16px', borderColor: '#f0f0f0' }} />

            {/* ── Customer ── */}
            <section style={{ marginBottom: '16px', fontSize: '13px' }}>

                {/* Row: label left · name right */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{
                        fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                        color: '#8c8c8c', letterSpacing: '0.6px'
                    }}>
                        Deliver To
                    </span>
                    <span style={{ fontWeight: 700, fontSize: '14px', color: '#1a1a1a' }}>
                        {order?.customer?.name || '—'}
                    </span>
                </div>

                {/* Contact details — icons + single line each */}
                <div style={{
                    display: 'flex', flexDirection: 'row', gap: '20px', flexWrap: 'wrap',
                    padding: '10px 12px',
                    borderRadius: '6px',
                }}>
                    {/* WhatsApp */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#595959' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" >
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        <span>{order?.customer?.whatsapp_number || '—'}</span>
                    </div>

                    {/* Email */}
                    {order?.customer?.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#595959' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#595959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="4" width="20" height="16" rx="2"/>
                                <polyline points="2,4 12,13 22,4"/>
                            </svg>
                            <span>{order?.customer?.email}</span>
                        </div>
                    )}

                    {/* Address */}
                    {order?.customer?.address && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#595959' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#595959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '1px', flexShrink: 0 }}>
                                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/>
                                <circle cx="12" cy="10" r="3"/>
                            </svg>
                            <span>{order?.customer?.address}</span>
                        </div>
                    )}
                </div>
            </section>

            {/* Items Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '16px' }}>
                <thead>
                    <tr style={{ backgroundColor: '#595959b4', color: '#fff' }}>
                        <th style={{ padding: '8px', textAlign: 'left',  width: '5%',  borderBottom: '1px solid #595959'  }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left',                borderBottom: '1px solid #595959'  }}>Item</th>
                        <th style={{ padding: '8px', textAlign: 'left',  width: '12%', borderBottom: '1px solid #595959'  }}>Code</th>
                        <th style={{ padding: '8px', textAlign: 'right', width: '12%', borderBottom: '1px solid #595959'  }}>Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', width: '13%', borderBottom: '1px solid #595959'  }}>Unit Price</th>
                        <th style={{ padding: '8px', textAlign: 'right', width: '10%', borderBottom: '1px solid #595959'  }}>Disc</th>
                        <th style={{ padding: '8px', textAlign: 'right', width: '14%', borderBottom: '1px solid #595959'  }}>Total (AED)</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '8px', color: '#8c8c8c' }}>{i + 1}</td>
                            <td style={{ padding: '8px', color: '#1a1a1a' }}>{getItemName(item)}</td>
                            <td style={{ padding: '8px', color: '#8c8c8c' }}>{getItemCode(item)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', color: '#595959' }}>{getItemQty(item)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', color: '#595959' }}>{getUnitPrice(item)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', color: '#ff4d4f' }}>
                                {hasDiscount(item) ? `-${parseFloat(item.discount_amount).toFixed(2)}` : '—'}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1a1a1a' }}>{getItemTotal(item)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Totals */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <div style={{ width: '280px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#595959' }}>
                        <span>Sub Total:</span><span>{formatAED(subtotal)}</span>
                    </div>
                    {discount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#ff4d4f' }}>
                            <span>Discount:</span><span>-{formatAED(discount)}</span>
                        </div>
                    )}
                    <hr style={{ borderColor: '#f0f0f0', margin: '6px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontWeight: 700, fontSize: '15px', color: '#1a1a1a' }}>
                        <span>GRAND TOTAL:</span><span>{formatAED(total)}</span>
                    </div>
                </div>
            </div>


            {/* Footer */}
            <footer style={{ borderTop: '1px solid #f0f0f0', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#bfbfbf' }}>
                <span>Thank you for your order!</span>
                <span>Printed: {formatDubaiDateTime()}</span>
            </footer>
        </div>
    )
})

ThermalOrderLayout.propTypes = { order: PropTypes.object.isRequired, size: PropTypes.oneOf(['58', '80']) }
A4OrderLayout.propTypes      = { order: PropTypes.object.isRequired }